import { z } from 'zod';
// =============================================================================
// Bank connections (super-admin surface)
// =============================================================================
export const BankConnectionProviderSchema = z.enum(['INVESTEC', 'ABSA']);
export const BankConnectionStatusSchema = z.enum(['ACTIVE', 'NEEDS_REAUTH']);
export const BankConnectionErrorCodeSchema = z.enum(['CREDENTIAL_REJECTED', 'UPSTREAM_FAILURE']);
// Deliberately loose: the provider's own schema decides what a well-formed
// secret is, and reports a failure by field path. A pattern here would put the
// rejected value through the generic validation error path instead.
const CredentialSecretSchema = z.string().min(1).max(512);
/** Investec Programmable Banking API key credentials, as the account holder mints them. */
export const InvestecApiKeyCredentialsInputSchema = z.strictObject({
    clientId: CredentialSecretSchema,
    clientSecret: CredentialSecretSchema,
    apiKey: CredentialSecretSchema,
});
/** A PEM block can run to a few kilobytes once a certificate carries its chain. */
const PemInputSchema = z.string().min(1).max(65_536);
/**
 * ABSA Access corporate credentials: the OAuth client, the API user whose
 * password the token grant sends, the gateway base URL ABSA issued, and the
 * client certificate and key extracted from ABSA's PKCS#12 bundle as PEM.
 */
export const AbsaAccessCredentialsInputSchema = z.strictObject({
    clientId: CredentialSecretSchema,
    username: CredentialSecretSchema,
    password: CredentialSecretSchema,
    scope: CredentialSecretSchema,
    gatewayBaseUrl: CredentialSecretSchema,
    clientCertificatePem: PemInputSchema,
    clientPrivateKeyPem: PemInputSchema,
    clientPrivateKeyPassphrase: CredentialSecretSchema.optional(),
});
/** Credentials per provider, discriminated so a second provider adds a member, not a reshape. */
export const BankConnectionCredentialsInputSchema = z.discriminatedUnion('provider', [
    z.strictObject({ provider: z.literal('INVESTEC'), credentials: InvestecApiKeyCredentialsInputSchema }),
    z.strictObject({ provider: z.literal('ABSA'), credentials: AbsaAccessCredentialsInputSchema }),
]);
const IdempotencyKeySchema = z.string().min(1).max(128);
export const ConnectBankConnectionInputSchema = z.discriminatedUnion('provider', [
    z.strictObject({
        provider: z.literal('INVESTEC'),
        credentials: InvestecApiKeyCredentialsInputSchema,
        idempotencyKey: IdempotencyKeySchema,
    }),
    z.strictObject({
        provider: z.literal('ABSA'),
        credentials: AbsaAccessCredentialsInputSchema,
        idempotencyKey: IdempotencyKeySchema,
    }),
]);
export const RotateBankConnectionCredentialsInputSchema = BankConnectionCredentialsInputSchema;
/** A connection as an operator sees it. Never carries the credential or its fingerprint. */
export const BankConnectionSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    provider: BankConnectionProviderSchema,
    status: BankConnectionStatusSchema,
    lastVerifiedAt: z.string().datetime(),
    lastErrorCode: BankConnectionErrorCodeSchema.nullable(),
    createdByUserId: z.string(),
    archivedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const BankConnectionResponseSchema = z.object({ data: BankConnectionSchema });
export const BankConnectionListResponseSchema = z.object({ data: z.array(BankConnectionSchema) });
/**
 * An account the credential can see at the bank, read live and never stored.
 * Only the last four digits of the account number leave the platform.
 */
export const BankConnectionProviderAccountSchema = z.object({
    providerAccountId: z.string(),
    name: z.string(),
    accountNumberLast4: z.string(),
    productName: z.string(),
});
export const BankConnectionProviderAccountListResponseSchema = z.object({
    data: z.array(BankConnectionProviderAccountSchema),
});
